/* eslint-disable no-irregular-whitespace */
import { apiClient } from '@/apis';
import { COUPON_TYPE_OPTIONS } from '@/config/constant';
import removeWhiteSpace from '@/utils/removeWhiteSpace';
import i18n from '@/i18n';
import { staticUrl } from '@/config';
import axios from 'axios';

export const buttonsMapToBackend = ({
  type,
  name,
  mobileLink,
  pcLink,
  iosDeepLink,
  androidDeepLink,
  bizFormKey,
}, index) => ({
  ordering: index + 1,
  type,
  name: removeWhiteSpace(name),
  linkMo: removeWhiteSpace(mobileLink),
  linkPc: removeWhiteSpace(pcLink),
  schemeIos: removeWhiteSpace(iosDeepLink),
  schemeAndroid: removeWhiteSpace(androidDeepLink),
  bizFormKey: removeWhiteSpace(bizFormKey),
});

export const buttonsMapToFrontend = ({
  type,
  name,
  linkMo,
  linkPc,
  schemeIos,
  schemeAndroid,
  bizFormKey,
}) => ({
  type,
  name: removeWhiteSpace(name || ''),
  bizFormKey: removeWhiteSpace(bizFormKey || ''),
  mobileLink: removeWhiteSpace(linkMo || ''),
  pcLink: removeWhiteSpace(linkPc || ''),
  iosDeepLink: removeWhiteSpace(schemeIos || ''),
  androidDeepLink: removeWhiteSpace(schemeAndroid || ''),
});

// 예전에 UI 언어 문구로 저장된 쿠폰 title 을 읽기 위한 언어 목록
const LEGACY_COUPON_TITLE_LOCALES = ['ko', 'en'];

/**
 * 쿠폰 유형과 쿠폰명으로 카카오 쿠폰 title 을 만든다. UI 언어와 상관없이 한글 형식이다.
 */
const toCouponTitle = (type, name) => {
  const option = COUPON_TYPE_OPTIONS.find((elm) => elm.type === type);

  if (!option) {
    return name || '';
  }

  return type === 'DELIVERY_DISCOUNT' ? option.titleSuffix : `${name}${option.titleSuffix}`;
};

/**
 * 쿠폰 title 에서 쿠폰 유형과 쿠폰명을 꺼낸다.
 * 한글 형식을 먼저 보고, 없으면 예전에 UI 언어 문구로 저장된 title 로 본다.
 */
const fromCouponTitle = (title = '') => {
  const option = COUPON_TYPE_OPTIONS.find((elm) => (elm.type === 'DELIVERY_DISCOUNT'
    ? title === elm.titleSuffix
    : title.endsWith(elm.titleSuffix)));

  if (option) {
    return {
      type: option.type,
      name: option.type === 'DELIVERY_DISCOUNT' ? '' : title.slice(0, -option.titleSuffix.length),
    };
  }

  const { t } = i18n.global;
  let result = { type: '', name: '' };

  LEGACY_COUPON_TITLE_LOCALES.some((locale) => COUPON_TYPE_OPTIONS.some((elm) => {
    const str = t(elm.text, {}, { locale }).replace('OO', '');

    if (title.match(str)) {
      result = { type: elm.type, name: title.replace(str, '') };
      return true;
    }

    return false;
  }));

  return result;
};

export const couponToBackend = ({
  enabled,
  type,
  name,
  description,
  mobileLink,
  pcLink,
  iosDeepLink,
  androidDeepLink,
}) => {
  if (!enabled) {
    return null;
  }

  return {
    title: removeWhiteSpace(toCouponTitle(type, name)),
    description: removeWhiteSpace(description),
    linkMo: removeWhiteSpace(mobileLink),
    linkPc: removeWhiteSpace(pcLink),
    schemeIos: removeWhiteSpace(iosDeepLink),
    schemeAndroid: removeWhiteSpace(androidDeepLink),
  };
};

export const couponToFrontend = (item) => {
  if (!item) {
    return { enabled: false };
  }

  const {
    title,
    description,
    schemeAndroid,
    schemeIos,
    linkMo,
    linkPc,
  } = item;

  const { type, name } = fromCouponTitle(title || '');

  return {
    enabled: true,
    type,
    title: title || '',
    description: description || '',
    name: name || '',
    mobileLink: linkMo || '',
    pcLink: linkPc || '',
    iosDeepLink: schemeIos || '',
    androidDeepLink: schemeAndroid || '',
  };
};

const tailToBackend = ({
  mobileLink,
  pcLink,
  iosDeepLink,
  androidDeepLink,
}) => ({
  linkMo: removeWhiteSpace(mobileLink),
  linkPc: removeWhiteSpace(pcLink),
  schemeIos: removeWhiteSpace(iosDeepLink),
  schemeAndroid: removeWhiteSpace(androidDeepLink),
});

const tailToFrontend = ({
  linkMo,
  linkPc,
  schemeIos,
  schemeAndroid,
}) => ({
  enabled: true,
  mobileLink: linkMo || '',
  pcLink: linkPc || '',
  iosDeepLink: schemeIos || '',
  androidDeepLink: schemeAndroid || '',
});

export const carouselFeedToBackend = ({
  list,
  tail,
}) => {
  const data = {
    carousel: list.map(({
      header,
      content,
      image,
      coupon,
      buttons,
    }) => ({
      header: removeWhiteSpace(header),
      message: removeWhiteSpace(content),
      imageId: image.id,
      coupon: couponToBackend(coupon),
      buttons: buttons.map(buttonsMapToBackend),
      imageLink: removeWhiteSpace(image.link),
    })),
  };

  if (tail?.enabled) {
    data.tail = tailToBackend(tail);
  }

  return data;
};

const getFriendtalkImageMeta = (id, imageType = 'friend') => new Promise((resolve, reject) => {
  const map = {
    friend: 'friendtalk',
    brand: 'brandtalk',
  };

  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = (err) => reject(err);

  axios.get(`${staticUrl}/kakao${map[imageType]}/${id}`, { responseType: 'blob' })
    .then((res) => {
      image.src = URL.createObjectURL(res.data);
    });
});

const getFriendtalkImageAllInfo = async (id, imageType = 'friend') => {
  const map = {
    friend: 'friendtalk',
    brand: 'brandtalk',
  };
  const promiseList = [];

  promiseList.push(apiClient.get(`/kakao/${map[imageType]}/image/${id}`));
  promiseList.push(getFriendtalkImageMeta(id, imageType));

  const [
    {
      name,
      type,
    },
    {
      width,
      height,
    },
  ] = await Promise.all(promiseList);

  return {
    id,
    name,
    type,
    width,
    height,
  };
};

export const carouselFeedToFrontend = async ({
  carousel,
  tail,
}) => {
  const promiseList = [];
  const imageObject = {};

  carousel.forEach(({ imageId }) => {
    if (imageId) {
      promiseList.push(getFriendtalkImageAllInfo(imageId));
    }
  });

  const responseList = await Promise.all(promiseList);

  responseList.forEach(({
    id,
    name,
    type,
    width,
    height,
  }) => {
    imageObject[id] = {
      id,
      name,
      width,
      height,
      ratio: height / width,
      url: `${staticUrl}/kakaofriendtalk/${id}`,
      type,
    };
  });

  const data = {
    index: 0,
    list: carousel.map(({
      header,
      message,
      imageId,
      coupon,
      buttons,
      imageLink,
    }) => ({
      header: header || '',
      content: message || '',
      image: {
        ...imageObject[imageId] ?? {},
        link: imageLink || '',
      },
      buttons: (buttons || []).map(buttonsMapToFrontend),
      coupon: couponToFrontend(coupon),
    })),
    tail: {},
  };

  if (tail) {
    data.tail = tailToFrontend(tail);
  }

  return data;
};

export const wideItemListToBackend = ({
  header,
  list,
  buttons,
  coupon,
}) => {
  const data = {
    header: removeWhiteSpace(header),
    item: list.map(({
      content,
      image,
      mobileLink,
      pcLink,
      iosDeepLink,
      androidDeepLink,
    }) => ({
      title: removeWhiteSpace(content),
      imageId: image?.id,
      linkMo: removeWhiteSpace(mobileLink),
      linkPc: removeWhiteSpace(pcLink),
      schemeIos: removeWhiteSpace(iosDeepLink),
      schemeAndroid: removeWhiteSpace(androidDeepLink),
    })),
    buttons: buttons.map(buttonsMapToBackend),
    coupon: couponToBackend(coupon),
  };

  return data;
};

export const wideItemListToFrontend = async ({
  header,
  item,
  buttons,
  coupon,
}) => {
  const promiseList = [];
  const imageObject = {};

  item.forEach(({ imageId }) => {
    if (imageId) {
      promiseList.push(getFriendtalkImageAllInfo(imageId));
    }
  });

  const responseList = await Promise.all(promiseList);

  responseList.forEach(({
    id,
    name,
    type,
    width,
    height,
  }) => {
    imageObject[id] = {
      id,
      name,
      width,
      height,
      ratio: height / width,
      url: `${staticUrl}/kakaofriendtalk/${id}`,
      type,
    };
  });

  const data = {
    index: 0,
    header,
    list: item.map(({
      title,
      imageId,
      linkMo,
      linkPc,
      schemeIos,
      schemeAndroid,
    }) => ({
      content: title || '',
      image: imageObject[imageId] ?? {},
      mobileLink: linkMo || '',
      pcLink: linkPc || '',
      iosDeepLink: schemeIos || '',
      androidDeepLink: schemeAndroid || '',
    })),
    buttons: (buttons || []).map(buttonsMapToFrontend),
    coupon: couponToFrontend(coupon),
  };

  return data;
};

export const carouselCommerceToBackend = ({
  list,
  tail,
}) => {
  const data = {
    intro: {
      enabled: list[0].introEnabled,
      header: removeWhiteSpace(list[0].header),
      content: removeWhiteSpace(list[0].content),
      imageId: list[0].image?.id,
      linkMo: removeWhiteSpace(list[0].mobileLink),
      linkPc: removeWhiteSpace(list[0].pcLink),
      schemeIos: removeWhiteSpace(list[0].iosDeepLink),
      schemeAndroid: removeWhiteSpace(list[0].androidDeepLink),
    },
    carousel: list.slice(1).map(({
      title,
      image,
      regularPrice,
      discountPrice,
      discountRate,
      discountFixed,
      additionalContent,
      buttons,
      coupon,
    }) => ({
      imageId: image?.id,
      imageLink: removeWhiteSpace(image?.link),
      commerce: {
        title: removeWhiteSpace(title),
        regularPrice: removeWhiteSpace(regularPrice),
        discountPrice: removeWhiteSpace(discountPrice),
        discountRate: removeWhiteSpace(discountRate),
        discountFixed: removeWhiteSpace(discountFixed),
      },
      additionalContent: removeWhiteSpace(additionalContent),
      buttons: buttons.map(buttonsMapToBackend),
      coupon: couponToBackend(coupon),
    })),
  };

  if (tail?.enabled) {
    data.tail = tailToBackend(tail);
  }

  if (!data.intro?.enabled) {
    delete data.intro;
  }

  return data;
};

export const carouselCommerceToFrontend = async ({
  intro,
  carousel,
  tail,
}) => {
  const promiseList = [];
  const imageObject = {};
  const list = [];

  if (intro?.imageId) {
    promiseList.push(getFriendtalkImageAllInfo(intro.imageId));
  }

  carousel.forEach(({ imageId }) => {
    if (imageId) {
      promiseList.push(getFriendtalkImageAllInfo(imageId));
    }
  });

  const responseList = await Promise.all(promiseList);

  responseList.forEach(({
    id,
    name,
    type,
    width,
    height,
  }) => {
    imageObject[id] = {
      id,
      name,
      url: `${staticUrl}/kakaofriendtalk/${id}`,
      type,
      width,
      height,
      ratio: height / width,
    };
  });

  list.push({
    introEnabled: !!intro?.header ?? false,
    header: intro?.header || '',
    content: intro?.content || '',
    image: imageObject[intro?.imageId] ?? {},
    mobileLink: intro?.linkMo || '',
    pcLink: intro?.linkPc || '',
    iosDeepLink: intro?.schemeIos || '',
    androidDeepLink: intro?.schemeAndroid || '',
  });

  carousel.forEach(({
    imageId,
    imageLink,
    additionalContent,
    commerce: {
      title,
      regularPrice,
      discountPrice,
      discountRate,
      discountFixed,
    },
    buttons,
    coupon,
  }) => {
    list.push({
      image: {
        ...imageObject[imageId] ?? {},
        link: imageLink || '',
      },
      title: title || '',
      regularPrice: regularPrice || '',
      discountPrice: discountPrice || '',
      discountRate: discountRate || '',
      discountFixed: discountFixed || '',
      additionalContent: additionalContent || '',
      buttons: (buttons || []).map(buttonsMapToFrontend),
      coupon: couponToFrontend(coupon),
    });
  });

  const data = {
    index: 0,
    list,
    tail: {},
  };

  if (tail) {
    data.tail = tailToFrontend(tail);
  }

  return data;
};

export const premiumVideoToBackend = ({
  header,
  content,
  coupon,
  buttons,
  videoUrl,
  imageId,
}) => {
  const data = {
    header: removeWhiteSpace(header),
    content: removeWhiteSpace(content),
    video: {
      url: videoUrl,
      imageId,
    },
    buttons: buttons.map(buttonsMapToBackend),
    coupon: couponToBackend(coupon),
  };

  return data;
};

export const premiumVideoToFrontend = async ({
  header,
  content,
  video,
  buttons,
  coupon,
}) => {
  const imageData = {};

  if (video?.imageId) {
    const temp = await getFriendtalkImageAllInfo(video.imageId);

    imageData.url = `${staticUrl}/kakaofriendtalk/${video.imageId}`;
    imageData.id = video?.imageId;
    imageData.name = temp.name;
    imageData.type = temp.type;
    imageData.width = temp.width;
    imageData.height = temp.height;
    imageData.ratio = temp.height / temp.width;
  }

  const data = {
    header: header || '',
    content: content || '',
    videoUrl: video?.url || '',
    imageId: video?.imageId,
    buttons: (buttons || []).map(buttonsMapToFrontend),
    coupon: couponToFrontend(coupon),
    imageName: imageData?.name,
    imageUrl: imageData?.url || '',
  };

  return data;
};

export const commerceToBackend = ({
  title,
  regularPrice,
  discountPrice,
  discountRate,
  discountFixed,
  additionalContent,
  image,
  buttons,
  coupon,
}) => {
  const data = {
    commerce: {
      title: removeWhiteSpace(title),
      regularPrice: removeWhiteSpace(regularPrice),
      discountPrice: removeWhiteSpace(discountPrice),
      discountRate: removeWhiteSpace(discountRate),
      discountFixed: removeWhiteSpace(discountFixed),
    },
    additionalContent: removeWhiteSpace(additionalContent),
    imageId: image?.id,
    imageLink: removeWhiteSpace(image?.link),
    buttons: buttons.map(buttonsMapToBackend),
    coupon: couponToBackend(coupon),
  };

  return data;
};

export const commerceToFrontend = async ({
  commerce: {
    title,
    regularPrice,
    discountPrice,
    discountRate,
    discountFixed,
  },
  additionalContent,
  imageId,
  imageLink,
  buttons,
  coupon,
}) => {
  const imageData = {};

  if (imageId) {
    const temp = await getFriendtalkImageAllInfo(imageId);

    imageData.id = imageId;
    imageData.url = `${staticUrl}/kakaofriendtalk/${imageId}`;
    imageData.name = temp.name;
    imageData.type = temp.type;
    imageData.width = temp.width;
    imageData.height = temp.height;
    imageData.ratio = temp.height / temp.width;
  }

  const data = {
    title: title || '',
    regularPrice: regularPrice || '',
    discountPrice: discountPrice || '',
    discountRate: discountRate || '',
    discountFixed: discountFixed || '',
    additionalContent: additionalContent || '',
    image: {
      ...imageData,
      link: imageLink || '',
    },
    buttons: (buttons || []).map(buttonsMapToFrontend),
    coupon: couponToFrontend(coupon),
  };

  return data;
};

export const toValidateData = ({
  messageType,
  textmessage,
  friendtalkDefault,
  friendtalkWideItemList,
  friendtalkCommerce,
  friendtalkCarouselFeed,
  friendtalkCarouselCommerce,
  friendtalkPremiumVideo,
  brandDefault,
  brandWideItemList,
  brandCommerce,
  brandCarouselFeed,
  brandCarouselCommerce,
  brandPremiumVideo,
}) => {
  const setButton = ({
    type,
    name,
    mobileLink,
    pcLink,
    iosDeepLink,
    androidDeepLink,
    bizFormKey,
  }) => ({
    messageType,
    type,
    name: removeWhiteSpace(name),
    mobileLink: removeWhiteSpace(mobileLink),
    pcLink: removeWhiteSpace(pcLink),
    iosDeepLink: removeWhiteSpace(iosDeepLink),
    androidDeepLink: removeWhiteSpace(androidDeepLink),
    bizFormKey: removeWhiteSpace(bizFormKey),
    checkAppLink: [
      mobileLink,
      iosDeepLink,
      androidDeepLink,
    ],
  });

  const setCoupon = ({
    enabled,
    type,
    name,
    description,
    mobileLink,
    pcLink,
    iosDeepLink,
    androidDeepLink,
  }) => {
    if (!enabled) {
      return {};
    }

    return {
      messageType,
      enabled,
      type,
      name: removeWhiteSpace(name),
      description: removeWhiteSpace(description),
      mobileLink: removeWhiteSpace(mobileLink),
      pcLink: removeWhiteSpace(pcLink),
      iosDeepLink: removeWhiteSpace(iosDeepLink),
      androidDeepLink: removeWhiteSpace(androidDeepLink),
      link: {
        mobileLink,
        pcLink,
        iosDeepLink,
        androidDeepLink,
      },
    };
  };

  switch (messageType) {
    case 'FRIENDTALK_TEXT':
    case 'FRIENDTALK_IMAGE':
    case 'FRIENDTALK_WIDE_IMAGE':
      return {
        image: friendtalkDefault.image?.id,
        content: removeWhiteSpace(friendtalkDefault.content),
        imageLink: removeWhiteSpace(friendtalkDefault.image?.link),
        buttons: (friendtalkDefault.buttons ?? []).map(setButton),
        coupon: setCoupon(friendtalkDefault.coupon),
        buttonCouponLength: {
          messageType,
          buttons: friendtalkDefault.buttons,
          coupon: friendtalkDefault.coupon,
        },
      };
    case 'BRANDTALK_TEXT':
    case 'BRANDTALK_IMAGE':
    case 'BRANDTALK_WIDE_IMAGE':
      return {
        image: brandDefault.image?.id,
        content: removeWhiteSpace(brandDefault.content),
        imageLink: removeWhiteSpace(brandDefault.image?.link),
        buttons: (brandDefault.buttons ?? []).map(setButton),
        coupon: setCoupon(brandDefault.coupon),
        buttonCouponLength: {
          messageType,
          buttons: brandDefault.buttons,
          coupon: brandDefault.coupon,
        },
      };
    case 'FRIENDTALK_WIDE_ITEM_LIST':
      return {
        header: friendtalkWideItemList.header,
        list: friendtalkWideItemList.list.map(({
          image,
          content,
          mobileLink,
          pcLink,
          iosDeepLink,
          androidDeepLink,
        }) => ({
          image: image?.id,
          content: removeWhiteSpace(content),
          mobileLink: removeWhiteSpace(mobileLink),
          pcLink: removeWhiteSpace(pcLink),
          iosDeepLink: removeWhiteSpace(iosDeepLink),
          androidDeepLink: removeWhiteSpace(androidDeepLink),
          link: {
            mobileLink,
            pcLink,
            iosDeepLink,
            androidDeepLink,
          },
        })),
        listLength: friendtalkWideItemList.list?.length ?? 0,
        buttons: (friendtalkWideItemList.buttons ?? []).map(setButton),
        coupon: setCoupon(friendtalkWideItemList.coupon),
        buttonCouponLength: {
          messageType,
          buttons: friendtalkWideItemList.buttons,
          coupon: friendtalkWideItemList.coupon,
        },
      };
    case 'BRANDTALK_WIDE_ITEM_LIST':
      return {
        header: brandWideItemList.header,
        list: brandWideItemList.list.map(({
          image,
          content,
          mobileLink,
          pcLink,
          iosDeepLink,
          androidDeepLink,
        }) => ({
          image: image?.id,
          content: removeWhiteSpace(content),
          mobileLink: removeWhiteSpace(mobileLink),
          pcLink: removeWhiteSpace(pcLink),
          iosDeepLink: removeWhiteSpace(iosDeepLink),
          androidDeepLink: removeWhiteSpace(androidDeepLink),
          link: {
            mobileLink,
            pcLink,
            iosDeepLink,
            androidDeepLink,
          },
        })),
        listLength: brandWideItemList.list?.length ?? 0,
        buttons: (brandWideItemList.buttons ?? []).map(setButton),
        coupon: setCoupon(brandWideItemList.coupon),
        buttonCouponLength: {
          messageType,
          buttons: brandWideItemList.buttons,
          coupon: brandWideItemList.coupon,
        },
      };
    case 'FRIENDTALK_COMMERCE':
      return {
        image: friendtalkCommerce.image?.id,
        title: removeWhiteSpace(friendtalkCommerce.title),
        regularPrice: removeWhiteSpace(friendtalkCommerce.regularPrice) || null,
        discountPrice: removeWhiteSpace(friendtalkCommerce.discountPrice) || null,
        discountRate: removeWhiteSpace(friendtalkCommerce.discountRate) || null,
        discountFixed: removeWhiteSpace(friendtalkCommerce.discountFixed) || null,
        commerce: {
          regularPrice: friendtalkCommerce.regularPrice || null,
          discountPrice: friendtalkCommerce.discountPrice || null,
          discountRate: friendtalkCommerce.discountRate || null,
          discountFixed: friendtalkCommerce.discountFixed || null,
        },
        additionalContent: removeWhiteSpace(friendtalkCommerce.additionalContent),
        imageLink: removeWhiteSpace(friendtalkCommerce.image?.link),
        buttons: (friendtalkCommerce.buttons ?? []).map(setButton),
        coupon: setCoupon(friendtalkCommerce.coupon),
        buttonCouponLength: {
          messageType,
          buttons: friendtalkCommerce.buttons,
          coupon: friendtalkCommerce.coupon,
        },
      };
    case 'BRANDTALK_COMMERCE':
      return {
        image: brandCommerce.image?.id,
        title: removeWhiteSpace(brandCommerce.title),
        regularPrice: removeWhiteSpace(brandCommerce.regularPrice) || null,
        discountPrice: removeWhiteSpace(brandCommerce.discountPrice) || null,
        discountRate: removeWhiteSpace(brandCommerce.discountRate) || null,
        discountFixed: removeWhiteSpace(brandCommerce.discountFixed) || null,
        commerce: {
          regularPrice: brandCommerce.regularPrice || null,
          discountPrice: brandCommerce.discountPrice || null,
          discountRate: brandCommerce.discountRate || null,
          discountFixed: brandCommerce.discountFixed || null,
        },
        additionalContent: removeWhiteSpace(brandCommerce.additionalContent),
        imageLink: removeWhiteSpace(brandCommerce.image?.link),
        buttons: (brandCommerce.buttons ?? []).map(setButton),
        coupon: setCoupon(brandCommerce.coupon),
        buttonCouponLength: {
          messageType,
          buttons: brandCommerce.buttons,
          coupon: brandCommerce.coupon,
        },
      };
    case 'FRIENDTALK_CAROUSEL_FEED':
      return {
        list: friendtalkCarouselFeed.list.map(({
          image,
          header,
          content,
          coupon,
          buttons,
        }) => ({
          image: image?.id,
          header: removeWhiteSpace(header),
          content: removeWhiteSpace(content),
          imageLink: removeWhiteSpace(image?.link),
          coupon: setCoupon(coupon),
          buttons: (buttons ?? []).map(setButton),
          buttonCouponLength: {
            messageType,
            buttons,
            coupon,
          },
        })),
        listLength: friendtalkCarouselFeed.list?.length ?? 0,
        tail: {
          enabled: friendtalkCarouselFeed.tail?.enabled,
          mobileLink: removeWhiteSpace(friendtalkCarouselFeed.tail?.mobileLink) || '',
          pcLink: removeWhiteSpace(friendtalkCarouselFeed.tail?.pcLink) || '',
          iosDeepLink: removeWhiteSpace(friendtalkCarouselFeed.tail?.iosDeepLink) || '',
          androidDeepLink: removeWhiteSpace(friendtalkCarouselFeed.tail?.androidDeepLink) || '',
        },
      };
    case 'BRANDTALK_CAROUSEL_FEED':
      return {
        list: brandCarouselFeed.list.map(({
          image,
          header,
          content,
          coupon,
          buttons,
        }) => ({
          image: image?.id,
          header: removeWhiteSpace(header),
          content: removeWhiteSpace(content),
          imageLink: removeWhiteSpace(image?.link),
          coupon: setCoupon(coupon),
          buttons: (buttons ?? []).map(setButton),
          buttonCouponLength: {
            messageType,
            buttons,
            coupon,
          },
        })),
        listLength: brandCarouselFeed.list?.length ?? 0,
        tail: {
          enabled: brandCarouselFeed.tail?.enabled,
          mobileLink: removeWhiteSpace(brandCarouselFeed.tail?.mobileLink) || '',
          pcLink: removeWhiteSpace(brandCarouselFeed.tail?.pcLink) || '',
          iosDeepLink: removeWhiteSpace(brandCarouselFeed.tail?.iosDeepLink) || '',
          androidDeepLink: removeWhiteSpace(brandCarouselFeed.tail?.androidDeepLink) || '',
        },
      };
    case 'FRIENDTALK_CAROUSEL_COMMERCE':
      return {
        list: friendtalkCarouselCommerce.list.map(({
          introEnabled,
          image,
          header,
          content,
          title,
          regularPrice,
          discountPrice,
          discountRate,
          discountFixed,
          additionalContent,
          buttons,
          coupon,
          mobileLink,
          pcLink,
          iosDeepLink,
          androidDeepLink,
        }, index) => {
          // intro type
          if (index === 0) {
            return {
              enabled: introEnabled,
              image: image?.id,
              header: removeWhiteSpace(header),
              content: removeWhiteSpace(content),
              mobileLink: removeWhiteSpace(mobileLink),
              pcLink: removeWhiteSpace(pcLink),
              iosDeepLink: removeWhiteSpace(iosDeepLink),
              androidDeepLink: removeWhiteSpace(androidDeepLink),
              link: {
                mobileLink,
                pcLink,
                iosDeepLink,
                androidDeepLink,
              },
            };
          }

          return {
            image: image?.id,
            title: removeWhiteSpace(title),
            regularPrice: removeWhiteSpace(regularPrice) || null,
            discountPrice: removeWhiteSpace(discountPrice) || null,
            discountRate: removeWhiteSpace(discountRate) || null,
            discountFixed: removeWhiteSpace(discountFixed) || null,
            commerce: {
              regularPrice: regularPrice || null,
              discountPrice: discountPrice || null,
              discountRate: discountRate || null,
              discountFixed: discountFixed || null,
            },
            additionalContent: removeWhiteSpace(additionalContent),
            imageLink: removeWhiteSpace(image?.link),
            coupon: setCoupon(coupon),
            buttons: (buttons ?? []).map(setButton),
            buttonCouponLength: {
              messageType,
              buttons,
              coupon,
            },
          };
        }),
        ratio: [...new Set(friendtalkCarouselCommerce
          .list
          .map((elm) => elm.image?.ratio ?? 0)
          .filter((v) => v))]?.length,
        listLength: friendtalkCarouselCommerce.list,
        tail: {
          enabled: friendtalkCarouselCommerce.tail?.enabled,
          mobileLink: removeWhiteSpace(friendtalkCarouselCommerce.tail?.mobileLink) || '',
          pcLink: removeWhiteSpace(friendtalkCarouselCommerce.tail?.pcLink) || '',
          iosDeepLink: removeWhiteSpace(friendtalkCarouselCommerce.tail?.iosDeepLink) || '',
          androidDeepLink: removeWhiteSpace(friendtalkCarouselCommerce.tail?.androidDeepLink) || '',
        },
      };
    case 'BRANDTALK_CAROUSEL_COMMERCE':
      return {
        list: brandCarouselCommerce.list.map(({
          introEnabled,
          image,
          header,
          content,
          title,
          regularPrice,
          discountPrice,
          discountRate,
          discountFixed,
          additionalContent,
          buttons,
          coupon,
          mobileLink,
          pcLink,
          iosDeepLink,
          androidDeepLink,
        }, index) => {
          // intro type
          if (index === 0) {
            return {
              enabled: introEnabled,
              image: image?.id,
              header: removeWhiteSpace(header),
              content: removeWhiteSpace(content),
              mobileLink: removeWhiteSpace(mobileLink),
              pcLink: removeWhiteSpace(pcLink),
              iosDeepLink: removeWhiteSpace(iosDeepLink),
              androidDeepLink: removeWhiteSpace(androidDeepLink),
              link: {
                mobileLink,
                pcLink,
                iosDeepLink,
                androidDeepLink,
              },
            };
          }

          return {
            image: image?.id,
            title: removeWhiteSpace(title),
            regularPrice: removeWhiteSpace(regularPrice) || null,
            discountPrice: removeWhiteSpace(discountPrice) || null,
            discountRate: removeWhiteSpace(discountRate) || null,
            discountFixed: removeWhiteSpace(discountFixed) || null,
            commerce: {
              regularPrice: regularPrice || null,
              discountPrice: discountPrice || null,
              discountRate: discountRate || null,
              discountFixed: discountFixed || null,
            },
            additionalContent: removeWhiteSpace(additionalContent),
            imageLink: removeWhiteSpace(image?.link),
            coupon: setCoupon(coupon),
            buttons: (buttons ?? []).map(setButton),
            buttonCouponLength: {
              messageType,
              buttons,
              coupon,
            },
          };
        }),
        ratio: [...new Set(brandCarouselCommerce
          .list
          .map((elm) => elm.image?.ratio ?? 0)
          .filter((v) => v))]?.length,
        listLength: brandCarouselCommerce.list,
        tail: {
          enabled: brandCarouselCommerce.tail?.enabled,
          mobileLink: removeWhiteSpace(brandCarouselCommerce.tail?.mobileLink) || '',
          pcLink: removeWhiteSpace(brandCarouselCommerce.tail?.pcLink) || '',
          iosDeepLink: removeWhiteSpace(brandCarouselCommerce.tail?.iosDeepLink) || '',
          androidDeepLink: removeWhiteSpace(brandCarouselCommerce.tail?.androidDeepLink) || '',
        },
      };
    case 'FRIENDTALK_PREMIUM_VIDEO':
      return {
        video: removeWhiteSpace(friendtalkPremiumVideo.videoUrl),
        image: friendtalkPremiumVideo.imageId || '',
        header: removeWhiteSpace(friendtalkPremiumVideo.header) || '',
        content: removeWhiteSpace(friendtalkPremiumVideo.content) || '',
        buttons: (friendtalkPremiumVideo.buttons ?? []).map(setButton),
        coupon: setCoupon(friendtalkPremiumVideo.coupon),
        buttonCouponLength: {
          messageType,
          buttons: friendtalkPremiumVideo.buttons,
          coupon: friendtalkPremiumVideo.coupon,
        },
      };
    case 'BRANDTALK_PREMIUM_VIDEO':
      return {
        video: removeWhiteSpace(brandPremiumVideo.videoUrl),
        image: brandPremiumVideo.imageId || '',
        header: removeWhiteSpace(brandPremiumVideo.header) || '',
        content: removeWhiteSpace(brandPremiumVideo.content) || '',
        buttons: (brandPremiumVideo.buttons ?? []).map(setButton),
        coupon: setCoupon(brandPremiumVideo.coupon),
        buttonCouponLength: {
          messageType,
          buttons: brandPremiumVideo.buttons,
          coupon: brandPremiumVideo.coupon,
        },
      };
    case 'SMS':
    case 'LMS':
    case 'MMS':
    default:
      return {
        subject: removeWhiteSpace(textmessage.subject) || '',
        content: removeWhiteSpace(textmessage.content) || '',
        image: textmessage.image?.id,
      };
  }
};

// brand
export const brandWideItemListToFrontend = async ({
  header,
  item,
  buttons,
  coupon,
}) => {
  const promiseList = [];
  const imageObject = {};

  item.forEach(({ imageId }) => {
    if (imageId) {
      promiseList.push(getFriendtalkImageAllInfo(imageId, 'brand'));
    }
  });

  const responseList = await Promise.all(promiseList);

  responseList.forEach(({
    id,
    name,
    type,
    width,
    height,
  }) => {
    imageObject[id] = {
      id,
      name,
      width,
      height,
      ratio: height / width,
      url: `${staticUrl}/kakaobrandtalk/${id}`,
      type,
    };
  });

  const data = {
    index: 0,
    header,
    list: item.map(({
      title,
      imageId,
      linkMo,
      linkPc,
      schemeIos,
      schemeAndroid,
    }) => ({
      content: title || '',
      image: imageObject[imageId] ?? {},
      mobileLink: linkMo || '',
      pcLink: linkPc || '',
      iosDeepLink: schemeIos || '',
      androidDeepLink: schemeAndroid || '',
    })),
    buttons: (buttons || []).map(buttonsMapToFrontend),
    coupon: couponToFrontend(coupon),
  };

  return data;
};

export const brandCommerceToFrontend = async ({
  commerce: {
    title,
    regularPrice,
    discountPrice,
    discountRate,
    discountFixed,
  },
  additionalContent,
  imageId,
  imageLink,
  buttons,
  coupon,
}) => {
  const imageData = {};

  if (imageId) {
    const temp = await getFriendtalkImageAllInfo(imageId, 'brand');

    imageData.id = imageId;
    imageData.url = `${staticUrl}/kakaobrandtalk/${imageId}`;
    imageData.name = temp.name;
    imageData.type = temp.type;
    imageData.width = temp.width;
    imageData.height = temp.height;
    imageData.ratio = temp.height / temp.width;
  }

  const data = {
    title: title || '',
    regularPrice: regularPrice || '',
    discountPrice: discountPrice || '',
    discountRate: discountRate || '',
    discountFixed: discountFixed || '',
    additionalContent: additionalContent || '',
    image: {
      ...imageData,
      link: imageLink || '',
    },
    buttons: (buttons || []).map(buttonsMapToFrontend),
    coupon: couponToFrontend(coupon),
  };

  return data;
};

export const brandCarouselFeedToFrontend = async ({
  carousel,
  tail,
}) => {
  const promiseList = [];
  const imageObject = {};

  carousel.forEach(({ imageId }) => {
    if (imageId) {
      promiseList.push(getFriendtalkImageAllInfo(imageId, 'brand'));
    }
  });

  const responseList = await Promise.all(promiseList);

  responseList.forEach(({
    id,
    name,
    type,
    width,
    height,
  }) => {
    imageObject[id] = {
      id,
      name,
      width,
      height,
      ratio: height / width,
      url: `${staticUrl}/kakaobrandtalk/${id}`,
      type,
    };
  });

  const data = {
    index: 0,
    list: carousel.map(({
      header,
      message,
      imageId,
      coupon,
      buttons,
      imageLink,
    }) => ({
      header: header || '',
      content: message || '',
      image: {
        ...imageObject[imageId] ?? {},
        link: imageLink || '',
      },
      buttons: (buttons || []).map(buttonsMapToFrontend),
      coupon: couponToFrontend(coupon),
    })),
    tail: {},
  };

  if (tail) {
    data.tail = tailToFrontend(tail);
  }

  return data;
};

export const brandCarouselCommerceToFrontend = async ({
  intro,
  carousel,
  tail,
}) => {
  const promiseList = [];
  const imageObject = {};
  const list = [];

  if (intro?.imageId) {
    promiseList.push(getFriendtalkImageAllInfo(intro.imageId, 'brand'));
  }

  carousel.forEach(({ imageId }) => {
    if (imageId) {
      promiseList.push(getFriendtalkImageAllInfo(imageId, 'brand'));
    }
  });

  const responseList = await Promise.all(promiseList);

  responseList.forEach(({
    id,
    name,
    type,
    width,
    height,
  }) => {
    imageObject[id] = {
      id,
      name,
      url: `${staticUrl}/kakaobrandtalk/${id}`,
      type,
      width,
      height,
      ratio: height / width,
    };
  });

  list.push({
    introEnabled: !!intro?.header ?? false,
    header: intro?.header || '',
    content: intro?.content || '',
    image: imageObject[intro?.imageId] ?? {},
    mobileLink: intro?.linkMo || '',
    pcLink: intro?.linkPc || '',
    iosDeepLink: intro?.schemeIos || '',
    androidDeepLink: intro?.schemeAndroid || '',
  });

  carousel.forEach(({
    imageId,
    imageLink,
    additionalContent,
    commerce: {
      title,
      regularPrice,
      discountPrice,
      discountRate,
      discountFixed,
    },
    buttons,
    coupon,
  }) => {
    list.push({
      image: {
        ...imageObject[imageId] ?? {},
        link: imageLink || '',
      },
      title: title || '',
      regularPrice: regularPrice || '',
      discountPrice: discountPrice || '',
      discountRate: discountRate || '',
      discountFixed: discountFixed || '',
      additionalContent: additionalContent || '',
      buttons: (buttons || []).map(buttonsMapToFrontend),
      coupon: couponToFrontend(coupon),
    });
  });

  const data = {
    index: 0,
    list,
    tail: {},
  };

  if (tail) {
    data.tail = tailToFrontend(tail);
  }

  return data;
};

export const brandPremiumVideoToFrontend = async ({
  header,
  content,
  video,
  buttons,
  coupon,
}) => {
  const imageData = {};

  if (video?.imageId) {
    const temp = await getFriendtalkImageAllInfo(video.imageId, 'brand');

    imageData.url = `${staticUrl}/kakaobrandtalk/${video.imageId}`;
    imageData.id = video?.imageId;
    imageData.name = temp.name;
    imageData.type = temp.type;
    imageData.width = temp.width;
    imageData.height = temp.height;
    imageData.ratio = temp.height / temp.width;
  }

  const data = {
    header: header || '',
    content: content || '',
    videoUrl: video?.url || '',
    imageId: video?.imageId,
    buttons: (buttons || []).map(buttonsMapToFrontend),
    coupon: couponToFrontend(coupon),
    imageName: imageData?.name,
    imageUrl: imageData?.url || '',
  };

  return data;
};
